/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import { Button, CircularProgress, Grid, IconButton, Paper, TextField } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { useCallback, useEffect, useRef, useState } from "react";
import { getContactAttributes, updateContactAttributes } from "../services/ContactService";
import { getAllDispositions } from "../../dispositionManager/services/DispositionService";
import { PageLoadSpinner, EmptyPlaceholder, ReadOnlyLabel } from "../../../components";
import { UpdateContactForm } from "../components/UpdateContact";
import InputTooltip from "../../../components/InputTooltip/InputTooltip";
import { UPDATE_CONTACT_TOOLTIPS } from "../../../utility/toolTipMessages";
import { ConfirmDialog } from "../../../components/ConfirmDialog/ConfirmDialog";
import { checkIfValidUUID } from "../../../utility";
import { ShowSnackbar } from "../../../components/Common";

function UpdateContact() {
	const [searchContact, setSearchContact] = useState({ loading: false, value: "" }); //searching text
	const [exstAttributes, setExstAttributes] = useState({}); // to reset values.
	const [contactData, setContactData] = useState({ disposition: null, subDisposition: null, notes: "" });
	const [allDispositions, setAllDispositions] = useState([]); // Dispositions Lookups.
	const [loading, setLoading] = useState(false); // API calls progress
	const [errors, setErrors] = useState({ errContactId: "" }); // field wise errors
	const [isFormEdited, setFormEdited] = useState(false);
	const [formState, setFormState] = useState({ severity: "success", message: "" }); //Snackbar alert message states.
	const [showDialog, setShowDialog] = useState(false); // for confirmation for searching on unsaved changes

	const { errContactId } = errors; // Field validation states.
	const { disposition, subDisposition, notes } = contactData;
	const isTaskInProgress = !isFormEdited || loading || searchContact.loading;
	const dispositions = allDispositions; // Lookups
	const subDispositions = disposition?.isEnabled ? disposition?.subDispositions || [] : [subDisposition]; // Lookups

	const fetchDispositions = useCallback(async () => {
		try {
			let response = await getAllDispositions();
			if (Array.isArray(response)) {
				setAllDispositions(response);
			}
		} catch (error) {
			setErrors({ errForm: "Error while fetching Dispositions details." });
			setSnackbar("Failed to load dispositions.", "error");
		}
	}, []);

	// Seeting up the initial focus for search contact id
	const searchInpRef = useRef();
	useEffect(() => {
		const timeout = setTimeout(() => {
			searchInpRef.current.focus();
		}, 100);
		return () => clearTimeout(timeout);
	}, []);

	useEffect(() => {
		fetchDispositions();
	}, [fetchDispositions]);

	const resetSearch = (loading = false) => {
		setSearchContact({ ...searchContact, loading: loading });
		setErrors({ errContactId: "" });
	};

	const processExstAttributes = (data) => {
		let preDisposition = allDispositions.find((disp) => disp.dispositionId === data.hidden_dispositionId) || null;
		const preSubDisposition =
			preDisposition?.subDispositions?.find((disp) => disp.subDispositionId === data.hidden_subDispositionId) || null;
		setContactData({ disposition: preDisposition, subDisposition: preSubDisposition, notes: data.notes || "" });
	};

	const searchAttributes = () => {
		resetSearch(true);
		getContactAttributes(searchContact.value)
			.then((data) => {
				if (typeof data === "object" && !Array.isArray(data) && data != null) {
					setExstAttributes({ ...data, contactId: searchContact.value });
					setSearchContact({ ...searchContact, loading: false });
					setFormEdited(false);
					processExstAttributes(data);
				} else {
					throw new Error("Error occurred while loading contact.");
				}
			})
			.catch((err) => {
				if (err.code === 404) {
					setErrors({ errContactId: err.message + " Please try with a valid Contact ID" });
				} else {
					setSnackbar("Failed to load contact.", "error");
				}
				setSearchContact({ ...searchContact, loading: false });
			});
	};

	const setSnackbar = (message, severity) => {
		setFormState({ message, severity });
	};

	// Event handlers
	const handleOnSnackbarClose = () => {
		setFormState((cur) => ({ ...cur, message: "" }));
	};

	const handleSaveAttributesClick = () => {
		const { dispositionId = "", dispositionName = "" } = disposition || {};
		const { subDispositionId = "", subDispositionName = "" } = subDisposition || {};
		setLoading(true);
		const newAttributes = {
			disposition: dispositionName,
			subDisposition: subDispositionName,
			notes: notes,
			hidden_dispositionId: dispositionId,
			hidden_subDispositionId: subDispositionId,
		};
		updateContactAttributes(exstAttributes.contactId, newAttributes)
			.then(() => {
				setSnackbar("Failed to save contact.", "success");
				setFormEdited(false);
				setExstAttributes({ ...exstAttributes, ...newAttributes });
			})
			.catch((err) => {
				setSnackbar(err.message, "success");
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleDispositionSelection = (e, disp) => {
		if (disposition?.dispositionId === disp?.dispositionId) {
			return;
		}
		setContactData({ disposition: disp, subDisposition: null, notes: notes });
		setFormEdited(true);
	};

	const handleSubDispositionSelection = (e, disp) => {
		setContactData({ ...contactData, subDisposition: disp });
		setFormEdited(true);
	};

	const handleSearchClick = () => {
		if (searchContact.value?.trim() === exstAttributes.contactId?.trim()) {
			return;
		} else if (!searchContact.value || !checkIfValidUUID(searchContact.value?.trim())) {
			setErrors({ errContactId: "Please try again with a valid Contact ID" });
			return;
		} else if (isFormEdited) {
			setShowDialog(true);
			return;
		}
		searchAttributes();
	};

	const handleCancelClick = () => {
		processExstAttributes(exstAttributes);
		setFormEdited(false);
	};

	const handleNotesChanges = ({ target }) => {
		setContactData({ ...contactData, notes: target.value });
		setFormEdited(true);
	};

	const handleSearchIdChange = ({ target }) => {
		setSearchContact({ value: target.value?.trimStart() });
		setErrors({ errContactId: "" });
	};

	const handleDialogClose = (response) => {
		setShowDialog(false);
		if (response) {
			searchAttributes();
		}
	};

	// Inner component renderers.
	const contactSearchStateIcon = () => {
		if (searchContact.loading) {
			return (
				<IconButton aria-label="contact search in-progress" size="small" style={{ maxHeight: 48, padding: 6 }}>
					<CircularProgress size={20} thickness={3} value={100} color="inherit" />
				</IconButton>
			);
		} else {
			return (
				<IconButton
					aria-label="search contact"
					size="small"
					onClick={handleSearchClick}
					style={{ maxHeight: 48, padding: 6 }}
				>
					<SearchIcon size={20} />
				</IconButton>
			);
		}
	};

	return (
		<div style={{ width: "100%" }}>
			<div style={{ minWidth: 700, maxWidth: 1000, minHeight: 200 }}>
				{loading && <PageLoadSpinner />}
				<ConfirmDialog
					title="Are you sure?"
					confirmOpen={showDialog}
					message="There are unsaved changes. Your changes will be discarded."
					onClose={handleDialogClose}
				/>
				<div style={{ marginTop: 30 }}>
					<div>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<Paper>
									<div style={{ padding: "15px 10px 15px 10px" }}>
										<div
											className="d-flex align-items-center justify-content-between"
											style={{ padding: "0 15px 5px", marginTop: 8 }}
										>
											<Grid item xs={8} md={6} className="d-flex">
												<TextField
													fullWidth
													type="text"
													size="small"
													label="Search Contact ID"
													error={errContactId.length > 0}
													helperText={errContactId}
													value={searchContact.value}
													inputRef={searchInpRef}
													onChange={handleSearchIdChange}
													InputProps={{
														startAdornment: <InputTooltip title={UPDATE_CONTACT_TOOLTIPS.SEARCH_CONTACT_ID_TOOLTIP} />,
														endAdornment: contactSearchStateIcon(),
													}}
													onKeyPress={(e) => e.key === "Enter" && handleSearchClick()}
												/>
											</Grid>
											{exstAttributes.contactId ? (
												<div>
													<Button
														variant="outlined"
														size="small"
														onClick={handleCancelClick}
														disabled={isTaskInProgress}
													>
														Cancel
													</Button>
													<Button
														variant="contained"
														disabled={isTaskInProgress}
														size="small"
														onClick={handleSaveAttributesClick}
														style={{
															backgroundColor: isTaskInProgress ? "#cc1e1e80" : "#cc1f20",
															color: "#fff",
															borderColor: "#cc1f20",
															marginLeft: 10,
														}}
													>
														Save
													</Button>
												</div>
											) : (
												""
											)}
										</div>
										<div style={{ minHeight: 200 }}>
											{exstAttributes.contactId ? (
												<>
													<ReadOnlyLabel label="Contact ID :" value={exstAttributes.contactId} />
													<ReadOnlyLabel label="Username :" value={exstAttributes.userName} />
													<UpdateContactForm
														loading={loading}
														dispositions={dispositions}
														subDispositions={subDispositions}
														disposition={disposition}
														subDisposition={subDisposition}
														notes={notes}
														handleDispositionSelection={handleDispositionSelection}
														handleSubDispositionSelection={handleSubDispositionSelection}
														handleNotesChanges={handleNotesChanges}
													/>
												</>
											) : (
												<EmptyPlaceholder message="To get started, search for a Contact ID to see the contact details." />
											)}
										</div>
									</div>
								</Paper>
							</Grid>
						</Grid>
					</div>
				</div>
			</div>
			<ShowSnackbar {...formState} onClose={handleOnSnackbarClose} />
		</div>
	);
}

export default UpdateContact;
